import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { projectsAPI } from '../services/api';

export default function Dashboard() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProject, setNewProject] = useState({
    name: '',
    address: '',
    loanAmount: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [creditFilter, setCreditFilter] = useState('All');
  const [loanAmountFilter, setLoanAmountFilter] = useState('All');
  const [interestRateFilter, setInterestRateFilter] = useState('All');
  const [locationFilter, setLocationFilter] = useState('All');
  const [sortBy, setSortBy] = useState('');

  // Mock loan marketplace data with card-based design
  const loanData = [
    {
      id: 1,
      companyName: 'ABC Construction',
      constructScore: 720,
      creditColor: 'green',
      requestedAmount: 10000000,
      projectType: 'Multifamily',
      location: 'Austin, TX',
      icon: 'wrench'
    },
    {
      id: 2,
      companyName: 'XYZ Builders',
      constructScore: 650,
      creditColor: 'yellow',
      requestedAmount: 750000,
      projectType: 'Multifamily',
      location: 'Dallas, TX',
      icon: 'wrench'
    },
    {
      id: 3,
      companyName: 'LMN Services',
      constructScore: 580,
      creditColor: 'red',
      requestedAmount: 500000,
      projectType: 'Multifamily',
      location: 'Houston, TX',
      icon: 'building'
    },
    {
      id: 4,
      companyName: 'Denver Developers',
      constructScore: 680,
      creditColor: 'yellow',
      requestedAmount: 2500000,
      projectType: 'Commercial',
      location: 'Denver, CO',
      icon: 'building'
    },
    {
      id: 5,
      companyName: 'Phoenix Projects',
      constructScore: 750,
      creditColor: 'green',
      requestedAmount: 8000000,
      projectType: 'Industrial',
      location: 'Phoenix, AZ',
      icon: 'wrench'
    }
  ];

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await projectsAPI.getAll();
      setProjects(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      await projectsAPI.create({
        ...newProject,
        loanAmount: parseFloat(newProject.loanAmount)
      });
      alert('Project created!');
      setShowNewProject(false);
      setNewProject({ name: '', address: '', loanAmount: '' });
      fetchProjects();
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to create project');
    }
  };

  const handleLogout = () => {
    auth.signOut();
  };

  const handleBid = (loanId) => {
    alert(`Bidding on loan ${loanId}`);
  };

  const getCreditColorClass = (creditScore) => {
    if (creditScore >= 700) return '#10B981'; // Green
    if (creditScore >= 600) return '#F59E0B'; // Yellow
    return '#EF4444'; // Red
  };

  const getConstructScoreLabel = (constructScore) => {
    if (constructScore >= 700) return 'Green';
    if (constructScore >= 600) return 'Yellow';
    return 'Red';
  };

  // Filter loans based on selected filters
  const filteredLoans = loanData.filter(loan => {
    // Search filter
    const matchesSearch = searchTerm === '' || 
      loan.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      loan.projectType.toLowerCase().includes(searchTerm.toLowerCase()) ||
      loan.requestedAmount.toString().includes(searchTerm);

    // ConstructScore filter
    const matchesCredit = creditFilter === 'All' || getConstructScoreLabel(loan.constructScore) === creditFilter;

    // Loan amount filter
    const matchesLoanAmount = loanAmountFilter === 'All' || (
      loanAmountFilter === '0-500k' && loan.requestedAmount <= 500000 ||
      loanAmountFilter === '500k-1m' && loan.requestedAmount > 500000 && loan.requestedAmount <= 1000000 ||
      loanAmountFilter === '1m-5m' && loan.requestedAmount > 1000000 && loan.requestedAmount <= 5000000 ||
      loanAmountFilter === '5m+' && loan.requestedAmount > 5000000
    );

    // Interest rate filter (mock - using construct score as proxy)
    const matchesInterestRate = interestRateFilter === 'All' || (
      interestRateFilter === '0-5' && loan.constructScore >= 750 ||
      interestRateFilter === '5-10' && loan.constructScore >= 650 && loan.constructScore < 750 ||
      interestRateFilter === '10-15' && loan.constructScore >= 550 && loan.constructScore < 650 ||
      interestRateFilter === '15+' && loan.constructScore < 550
    );

    // Location filter
    const matchesLocation = locationFilter === 'All' || loan.location === locationFilter;

    return matchesSearch && matchesCredit && matchesLoanAmount && matchesInterestRate && matchesLocation;
  });

  // Sort filtered loans
  const sortedLoans = [...filteredLoans].sort((a, b) => {
    switch(sortBy) {
      case 'amount-high': return b.requestedAmount - a.requestedAmount;
      case 'amount-low': return a.requestedAmount - b.requestedAmount;
      case 'construct-high': return b.constructScore - a.constructScore;
      case 'construct-low': return a.constructScore - b.constructScore;
      default: return 0;
    }
  });

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#000000' }}>
        <div style={{ color: 'white' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#000000', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <header style={{ 
        padding: '2rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <h1 style={{ 
          fontSize: '2.5rem', 
          fontWeight: 'bold', 
          color: 'white',
          margin: 0
        }}>
          Loan Marketplace
        </h1>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>{/* CFO Dashboard Button */}
          <button
            onClick={() => navigate('/cfo-dashboard')}
            style={{
              backgroundColor: '#10B981',
              color: 'white',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              border: 'none',
              fontSize: '1rem',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            💼 CFO Insights
          </button>
          {/* Pre-Approvals Button */}
          <button
            onClick={() => navigate('/pre-approvals')}
            style={{
              backgroundColor: '#8B5CF6',
              color: 'white',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              border: 'none',
              fontSize: '1rem',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Pre-Approvals
          </button>

          {/* Project Listings Button */}
          <button
            onClick={() => navigate('/project-listings')}
            style={{
              backgroundColor: '#8B5CF6',
              color: 'white',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              border: 'none',
              fontSize: '1rem',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Project Listings
          </button>

          {/* Logout Button */}
          <button
            onClick={handleLogout}
            style={{
              backgroundColor: '#EF4444',
              color: 'white',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              border: 'none',
              fontSize: '1rem',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Logout
          </button>

          {/* Search Bar */}
          <input
            type="text"
            placeholder="Search by contractor or project type or loan amount"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: '0.75rem 1rem',
              backgroundColor: '#2D2D2D',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              fontSize: '0.875rem',
              minWidth: '300px'
            }}
          />

          {/* Filter Dropdowns */}
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <select
              value={creditFilter}
              onChange={(e) => setCreditFilter(e.target.value)}
              style={{
                padding: '0.75rem',
                backgroundColor: '#2D2D2D',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontSize: '0.875rem'
              }}
            >
              <option value="All">ConstructScore: All</option>
              <option value="Green">ConstructScore: Green</option>
              <option value="Yellow">ConstructScore: Yellow</option>
              <option value="Red">ConstructScore: Red</option>
            </select>

            <select
              value={loanAmountFilter}
              onChange={(e) => setLoanAmountFilter(e.target.value)}
              style={{
                padding: '0.75rem',
                backgroundColor: '#2D2D2D',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontSize: '0.875rem'
              }}
            >
              <option value="All">Loan Amount: All</option>
              <option value="0-500k">$0 - $500K</option>
              <option value="500k-1m">$500K - $1M</option>
              <option value="1m-5m">$1M - $5M</option>
              <option value="5m+">$5M+</option>
            </select>

            <select
              value={interestRateFilter}
              onChange={(e) => setInterestRateFilter(e.target.value)}
              style={{
                padding: '0.75rem',
                backgroundColor: '#2D2D2D',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontSize: '0.875rem'
              }}
            >
              <option value="All">Interest Rate: All</option>
              <option value="0-5">0% - 5%</option>
              <option value="5-10">5% - 10%</option>
              <option value="10-15">10% - 15%</option>
              <option value="15+">15%+</option>
            </select>

            <select
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              style={{
                padding: '0.75rem',
                backgroundColor: '#2D2D2D',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontSize: '0.875rem'
              }}
            >
              <option value="All">Location: All</option>
              <option value="Austin, TX">Austin, TX</option>
              <option value="Dallas, TX">Dallas, TX</option>
              <option value="Houston, TX">Houston, TX</option>
              <option value="Denver, CO">Denver, CO</option>
              <option value="Phoenix, AZ">Phoenix, AZ</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                padding: '0.75rem',
                backgroundColor: '#2D2D2D',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontSize: '0.875rem'
              }}
            >
              <option value="">Sort By</option>
              <option value="amount-high">Amount (High to Low)</option>
              <option value="amount-low">Amount (Low to High)</option>
              <option value="construct-high">ConstructScore (High to Low)</option>
              <option value="construct-low">ConstructScore (Low to High)</option>
            </select>
          </div>
        </div>
      </header>

      {/* Main Content - Loan Cards */}
      <main style={{ padding: '0 2rem 2rem 2rem', flex: 1 }}>
        {sortedLoans.length === 0 ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '4rem 2rem',
            color: 'white',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔍</div>
            <h3 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>No loans found</h3>
            <p style={{ color: '#999', fontSize: '1rem' }}>
              Try adjusting your filters or search terms to find more opportunities.
            </p>
          </div>
        ) : (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', 
            gap: '2rem',
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            {sortedLoans.map((loan) => (
            <div key={loan.id} style={{
              backgroundColor: '#1A1A1A',
              borderRadius: '12px',
              padding: '1.5rem',
              borderTop: `4px solid ${getCreditColorClass(loan.constructScore)}`,
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem'
            }}>
              {/* Company Name and Icon */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ color: 'white', fontSize: '1.25rem', fontWeight: 'bold', margin: 0 }}>
                  {loan.companyName}
                </h3>
                <div style={{ color: 'white' }}>
                  {loan.icon === 'wrench' ? (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                      <path d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z"/>
                    </svg>
                  ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                      <path d="M12 3L2 12h3v8h6v-6h2v6h6v-8h3L12 3z"/>
                    </svg>
                  )}
                </div>
              </div>

              {/* ConstructScore */}
              <div style={{ color: 'white', fontSize: '0.875rem' }}>
                ConstructScore: {loan.constructScore} ({getConstructScoreLabel(loan.constructScore)})
              </div>

              {/* Requested Amount */}
              <div style={{ color: 'white', fontSize: '1.5rem', fontWeight: 'bold' }}>
                Requested: ${loan.requestedAmount.toLocaleString()}
              </div>

              {/* Project Type */}
              <div style={{ color: 'white', fontSize: '0.875rem' }}>
                Project Type: {loan.projectType}
              </div>

              {/* Location */}
              <div style={{ color: 'white', fontSize: '0.875rem' }}>
                📍 Location: {loan.location}
              </div>

              {/* Place Your Bid Button */}
              <button
                onClick={() => handleBid(loan.id)}
                style={{
                  backgroundColor: '#8B5CF6',
                  color: 'white',
                  padding: '0.875rem',
                  borderRadius: '8px',
                  border: 'none',
                  fontSize: '1rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  marginTop: 'auto'
                }}
              >
                Place Your Bid
              </button>

              {/* Expand Icon */}
              <div style={{ 
                position: 'absolute', 
                bottom: '1rem', 
                left: '1.5rem',
                color: 'white',
                cursor: 'pointer'
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                  <polyline points="6,9 12,15 18,9" stroke="white" strokeWidth="2" fill="none"/>
                </svg>
              </div>
            </div>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer style={{
        padding: '2rem',
        borderTop: '1px solid #333',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div style={{ color: '#999', fontSize: '0.875rem' }}>
          © 2024 ConstructPay. All rights reserved.
        </div>
        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
          <a href="#" style={{ color: 'white', fontSize: '0.875rem', textDecoration: 'none' }}>
            Bid History
          </a>
          <a href="#" style={{ color: 'white', fontSize: '0.875rem', textDecoration: 'none' }}>
            Loan Calculator
          </a>
          <a href="#" style={{ color: 'white', fontSize: '0.875rem', textDecoration: 'none' }}>
            Help
          </a>
          <a href="#" style={{ color: 'white', fontSize: '0.875rem', textDecoration: 'none' }}>
            Contact Support
          </a>
        </div>
      </footer>

      {/* New Project Modal */}
      {showNewProject && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ background: '#1A1A1A', borderRadius: '10px', padding: '2rem', maxWidth: '500px', width: '90%' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: 'white' }}>Create New Project</h2>
            <form onSubmit={handleCreateProject}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.25rem', color: 'white' }}>Project Name</label>
                <input
                  type="text"
                  required
                  value={newProject.name}
                  onChange={(e) => setNewProject({...newProject, name: e.target.value})}
                  style={{ width: '100%', padding: '0.5rem', backgroundColor: '#2D2D2D', border: 'none', borderRadius: '5px', color: 'white' }}
                  placeholder="Downtown Tower"
                />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.25rem', color: 'white' }}>Address</label>
                <input
                  type="text"
                  required
                  value={newProject.address}
                  onChange={(e) => setNewProject({...newProject, address: e.target.value})}
                  style={{ width: '100%', padding: '0.5rem', backgroundColor: '#2D2D2D', border: 'none', borderRadius: '5px', color: 'white' }}
                  placeholder="123 Main St"
                />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.25rem', color: 'white' }}>Loan Amount</label>
                <input
                  type="number"
                  required
                  value={newProject.loanAmount}
                  onChange={(e) => setNewProject({...newProject, loanAmount: e.target.value})}
                  style={{ width: '100%', padding: '0.5rem', backgroundColor: '#2D2D2D', border: 'none', borderRadius: '5px', color: 'white' }}
                  placeholder="5000000"
                />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button type="submit" style={{ flex: 1, background: '#8B5CF6', color: 'white', padding: '0.75rem', borderRadius: '5px', border: 'none', cursor: 'pointer' }}>
                  Create
                </button>
                <button type="button" onClick={() => setShowNewProject(false)} style={{ flex: 1, background: '#2D2D2D', color: 'white', padding: '0.75rem', borderRadius: '5px', border: 'none', cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}